import { GeneGrouping, L2Target, L2Targets } from '@resistapp/common/assays';
import { FullProject, FullSamplesByUID } from '@resistapp/common/types';
import { sortUniqSamplingPointsAndAddSampleStatus } from '@resistapp/common/utils';
import { Dictionary, isNil } from 'lodash';
import { theme } from './theme';

export function getGroupColor(
  group: string,
  grouping: GeneGrouping,
  allGeneGroups: Record<string, Array<L2Targets | string>>,
) {
  const fallbackColor = getFallbackGroupColor(group);

  if (group === 'All antibiotics') {
    return '#FFFFFF';
  } else if (grouping === 'l2Target' || grouping === 'antibiotic') {
    return Object.prototype.hasOwnProperty.call(targetPalette, group)
      ? targetPalette[group as L2Target]
      : fallbackColor;
  }

  const groupMembers = allGeneGroups[grouping] ?? [];
  if (groupMembers.length === 0) {
    return fallbackColor;
  }

  const groupIndex = groupMembers.indexOf(group);
  if (groupIndex < 0) {
    return fallbackColor;
  }

  return geneGroupingPalette[groupIndex % geneGroupingPalette.length];
}

export function getGroupTextColor(
  group: string,
  grouping: GeneGrouping,
  allGeneGroups: Record<string, Array<L2Targets | string>>,
) {
  const isAllAntibiotics = group === 'All antibiotics';
  const groupMembers = allGeneGroups[grouping];
  const isKnownPaletteTarget =
    isAllAntibiotics ||
    ((grouping === 'l2Target' || grouping === 'antibiotic') &&
      Object.prototype.hasOwnProperty.call(targetPalette, group));
  const isKnownGroupedTarget =
    grouping !== 'l2Target' && grouping !== 'antibiotic' && Array.isArray(groupMembers) && groupMembers.includes(group);

  const groupColor = getGroupColor(group, grouping, allGeneGroups);

  if (!isKnownPaletteTarget && !isKnownGroupedTarget) {
    return theme.colors.neutral900;
  }

  return getTextColor(groupColor);
}

export function getGroupPalette(
  groups: string[],
  grouping: GeneGrouping,
  allGeneGroups: Record<string, Array<L2Targets | string>>,
) {
  return groups.map(t => getGroupColor(t, grouping, allGeneGroups));
}

// Color map distinct sampling sampling points alphabetically by type and name:
export function getSmplPntColorById(project: FullProject): Dictionary<string> {
  return getSmplPntColorBySamplingPointId(project.samplesByUID, project.id);
}

export function getSmplPntColorBySamplingPointId(
  samplesByUID: FullSamplesByUID,
  projectId: number | undefined,
): Dictionary<string> {
  const samplingPoints = sortUniqSamplingPointsAndAddSampleStatus(samplesByUID, projectId);
  return samplingPoints.reduce<Dictionary<string>>((acc, smplPnt, envIdx) => {
    const paletteIdx = envIdx % envPalette.length;
    acc[smplPnt.id] = smplPnt.name === 'NTC' ? '#525252' : envPalette[paletteIdx];
    return acc;
  }, {});
}

export const targetPalette: { [key in L2Target]: string } = {
  MGE: '#ffa929',
  INTEGRONS: '#fa7900',
  BETA_LACTAM: '#B71C1B',
  VANCOMYCIN: '#911B51',
  MDR: '#2e081e',
  TRIMETHOPRIM: '#4C0C69',
  PHENICOL: '#0c461b',
  QUINOLONE: '#0f9916',
  SULFONAMIDE: '#487a48',
  TETRACYCLINE: '#2a9fc6',
  AMINOGLYCOSIDE: '#2c4e81',
  MLSB: '#1a2542',
  OTHER_RESISTANCE_MARKER: '#686868',
  PATHOGEN_MARKER: '#651324',
  OTHER_TAXONOMIC_MARKER: '#b66f35',
  OTHER_MICROBIAL_MARKER: '#9467BD',
  SIXTEENS_RRNA: '#0F0F0F',
};

const miscPalette: string[] = [
  '#293651',
  '#3D68E8',
  '#872B53',
  '#8d06b0',
  '#7498E8',
  '#DC8BB1',
  '#076113',
  '#54b693',
  '#487a48',
  '#2BB151',
  '#3f5322',
  '#929290',
  '#fecf0f',
  '#c7491f',
];

function getFallbackGroupColor(label: string) {
  const palette = miscPalette.length > 0 ? miscPalette : [theme.colors.neutral500];
  const hash = Math.abs(Array.from(label).reduce((acc, char) => (acc << 5) - acc + char.charCodeAt(0), 0));

  return palette[hash % palette.length];
}

export function getTextColor(
  hexBackgroundColorString: string,
  light = theme.colors.neutral100,
  dark = theme.colors.neutral900,
) {
  if (isNil(hexBackgroundColorString) || hexBackgroundColorString.length !== 7 || hexBackgroundColorString[0] !== '#') {
    console.error('Invalid hex color string:', hexBackgroundColorString, typeof hexBackgroundColorString);
    return dark; // Default to dark if invalid input
  }
  const r = parseInt(hexBackgroundColorString.substring(1, 3), 16);
  const g = parseInt(hexBackgroundColorString.substring(3, 5), 16);
  const b = parseInt(hexBackgroundColorString.substring(5, 7), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000; // magic intensity numbers by gpt-4
  return brightness < 128 ? light : dark;
}
const envPalette: string[] = miscPalette;
const geneGroupingPalette: string[] = [...miscPalette].reverse();
